// hooks/useCountdownTimer.ts
import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";

interface TimeType {
  minutes: number;
  seconds: number;
  milliseconds: number;
}

export function useFetchTimer() {
  const [time, setTime] = useState<TimeType>({
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchEndTime = useCallback(async (): Promise<Date | null> => {
    try {
      setIsLoading(true);
      const response = await fetch("https://api.catdevai.com/api/current-set-time/");
      const data = await response.json();
      const createdAt = new Date(data.created_at);
      const endTime = new Date(createdAt.getTime() + 60 * 60 * 1000);
      const now = new Date();
      return endTime <= now ? null : endTime;
    } catch (error) {
      toast.error(`Error: ${error instanceof Error ? error.message : error}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startCountdown = useCallback(async () => {
    const endTime = await fetchEndTime();

    if (!endTime) {
      setTime({ minutes: 0, seconds: 0, milliseconds: 0 });
      setTimeout(() => startCountdown(), 1000); // Retry
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();

      if (diff <= 0) {
        clearInterval(intervalRef.current!);
        setTime({ minutes: 0, seconds: 0, milliseconds: 0 });

        setTimeout(() => startCountdown(), 1000); // Refetch after 1s
      } else {
        setTime({
          minutes: Math.floor(diff / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
          milliseconds: Math.floor((diff % 1000) / 10),
        });
      }
    }, 10);
  }, [fetchEndTime]); 

  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startCountdown]);

  return { time, isLoading };
}
